"use client";

import React, { useState } from "react";
import { trackFormSubmissionGTM } from '@/lib/analytics'
import { getStoredUTMParams } from '@/lib/utm'
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Loader2, CheckCircle } from "lucide-react";
import { useSalesforceIdentity } from "@/components/consent/useSalesforceIdentity";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { RawButton } from "@/components/atoms/RawButton";
import { Input } from "@/components/atoms/input";

type FormState = 'idle' | 'submitting' | 'success' | 'error';

/**
 * StayInformedForm Component
 *
 * A compact newsletter signup form designed for article sidebars.
 * Submits to the /api/newsletter endpoint with reCAPTCHA support.
 */
export function StayInformedForm() {
    const [formState, setFormState] = useState<FormState>('idle');
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { sendIdentityEvent } = useSalesforceIdentity();

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const trimmedCompany = company.trim();
        if (!trimmedCompany) {
            setErrorMessage('Company Name is required');
            setFormState('error');
            return;
        }

        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setErrorMessage('Email is required');
            setFormState('error');
            return;
        }

        if (!validateEmail(trimmedEmail)) {
            setErrorMessage('Please enter a valid email address');
            setFormState('error');
            return;
        }

        setFormState('submitting');
        setErrorMessage('');

        const utmParams = getStoredUTMParams();

        try {
            // Get reCAPTCHA token if available
            let recaptchaToken: string | undefined;
            if (executeRecaptcha) {
                try {
                    recaptchaToken = await executeRecaptcha('stay_informed_signup');
                } catch (recaptchaError) {
                    console.warn('reCAPTCHA token generation failed:', recaptchaError);
                }
            }

            const response = await fetch('/api/forms/submit', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    formType: 'newsletter',
                    data: { email: trimmedEmail, company: trimmedCompany },
                    recaptchaToken,
                    pageUrl: window.location.href,
                    utmParams,
                }),
            });

            if (response.ok) {
                setFormState('success');
                sendIdentityEvent(trimmedEmail, 'newsletter_signup');
                setEmail('');
                setCompany('');
                trackFormSubmissionGTM('stay_informed', true, utmParams as unknown as Record<string, string>);
            } else {
                const data = await response.json();
                setErrorMessage(data.message || 'Something went wrong. Please try again.');
                setFormState('error');
                trackFormSubmissionGTM('stay_informed', false, utmParams as unknown as Record<string, string>);
            }
        } catch {
            setErrorMessage('An unexpected error occurred. Please try again.');
            setFormState('error');
            trackFormSubmissionGTM('stay_informed', false, utmParams as unknown as Record<string, string>);
        }
    };

    return (
        <Box className="p-10 rounded-sm bg-accent text-accent-foreground text-center space-y-6 shadow-2xl relative overflow-hidden transition-all duration-300 hover:shadow-orange-500/10">
            {/* Decorative blur element */}
            <Box className="absolute top-0 right-0 w-32 h-32 bg-primary/10 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2" />

            {formState === 'success' ? (
                <Box className="relative z-10 py-4">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                    <Text as="p" className="text-xl font-bold">Thank you!</Text>
                    <Text as="p" className="text-sm text-white/70 mt-2">You&apos;re now subscribed to our newsletter.</Text>
                </Box>
            ) : (
                <Box>
                    <Text as="p" className="text-2xl font-bold relative z-10">Stay Informed</Text>
                    <Text as="p" className="text-sm text-white/70 relative z-10">Get industry-leading insights delivered to your inbox.</Text>

                    <form onSubmit={handleSubmit} className="relative z-10 space-y-3" aria-label="Stay informed newsletter" role="form">
                        <label htmlFor="stayinformed-company" className="sr-only">Company Name</label>
                        <Input
                            id="stayinformed-company"
                            type="text"
                            aria-label="Company Name"
                            autoComplete="organization"
                            placeholder="Company Name"
                            value={company}
                            onChange={(e) => {
                                setCompany(e.target.value);
                                if (formState === 'error') {
                                    setFormState('idle');
                                    setErrorMessage('');
                                }
                            }}
                            disabled={formState === 'submitting'}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-sm disabled:opacity-50"
                        />
                        <label htmlFor="stayinformed-email" className="sr-only">Email Address</label>
                        <Input
                            id="stayinformed-email"
                            type="email"
                            aria-label="Email address"
                            autoComplete="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (formState === 'error') {
                                    setFormState('idle');
                                    setErrorMessage('');
                                }
                            }}
                            disabled={formState === 'submitting'}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-sm disabled:opacity-50"
                        />

                        {formState === 'error' && errorMessage && (
                            <Text as="p" className="text-red-300 text-xs text-left">
                                {errorMessage}
                            </Text>
                        )}

                        <RawButton
                            type="submit"
                            aria-label="Subscribe"
                            disabled={formState === 'submitting'}
                            className="w-full py-4 bg-primary text-primary-foreground font-bold rounded-sm hover:bg-primary/90 transition-all hover:scale-102 active:scale-98 uppercase tracking-widest text-sm shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                        >
                            {formState === 'submitting' ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Subscribing...
                                </>
                            ) : (
                                'Subscribe'
                            )}
                        </RawButton>
                    </form>
                </Box>
            )}
        </Box>
    );
}
